import { jsx as _jsx } from "nativewind/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image } from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import styled from 'styled-components/native';
import { color } from '@/constant/color';
// 메모리 캐시: URL → { width, height }
const imageSizeCache = new Map();
const RESIZE_MODE_MAP = {
    cover: FastImage.resizeMode.cover,
    contain: FastImage.resizeMode.contain,
    stretch: FastImage.resizeMode.stretch,
    center: FastImage.resizeMode.center,
};
export default function SccRemoteImage({ imageUrl, onReady, resizeMode = 'cover', style, wrapperBackgroundColor, fixedHeight, priority = 'normal', }) {
    const [ready, setReady] = useState(false);
    const [originalSize, setOriginalSize] = useState();
    const [measuredWidth, setMeasuredWidth] = useState();
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;
    const handleLayout = useCallback((event) => {
        setMeasuredWidth(event.nativeEvent.layout.width);
    }, []);
    // 캐시에서 사이즈 복원 + Image.getSize 폴백.
    // 일부 환경(특히 react-native-web)에서 FastImage `onLoad` 의
    // `e.nativeEvent.{width, height}` 가 undefined 로 들어와 사이즈 계산이
    // 실패하는 케이스가 있어, `Image.getSize` 로 사이즈를 확정 확보한다.
    useEffect(() => {
        setReady(false);
        const cached = imageSizeCache.get(imageUrl);
        if (cached) {
            setOriginalSize(cached);
            return;
        }
        setOriginalSize(undefined);
        if (!imageUrl)
            return;
        let cancelled = false;
        Image.getSize(imageUrl, (width, height) => {
            if (cancelled || !width || !height)
                return;
            imageSizeCache.set(imageUrl, { width, height });
            setOriginalSize({ width, height });
        }, () => {
            // 무시 - onError 에서 ready 처리
        });
        return () => {
            cancelled = true;
        };
    }, [imageUrl]);
    // onLoad에서 사이즈 확보, ready는 사이즈+레이아웃 준비 후
    const handleImageLoad = useCallback((e) => {
        var _a, _b, _c, _d, _e;
        const ne = e === null || e === void 0 ? void 0 : e.nativeEvent;
        const width = (_a = ne === null || ne === void 0 ? void 0 : ne.width) !== null && _a !== void 0 ? _a : (_b = ne === null || ne === void 0 ? void 0 : ne.source) === null || _b === void 0 ? void 0 : _b.width;
        const height = (_c = ne === null || ne === void 0 ? void 0 : ne.height) !== null && _c !== void 0 ? _c : (_d = ne === null || ne === void 0 ? void 0 : ne.source) === null || _d === void 0 ? void 0 : _d.height;
        if (width && height) {
            if (!imageSizeCache.has(imageUrl)) {
                imageSizeCache.set(imageUrl, { width, height });
            }
            setOriginalSize({ width, height });
        }
        if (measuredWidth !== undefined) {
            setReady(true);
            (_e = onReadyRef.current) === null || _e === void 0 ? void 0 : _e.call(onReadyRef);
        }
    }, [imageUrl, measuredWidth]);
    // onLayout 후 사이즈가 있으면 ready
    useEffect(() => {
        var _a;
        if (measuredWidth !== undefined && originalSize && !ready) {
            setReady(true);
            (_a = onReadyRef.current) === null || _a === void 0 ? void 0 : _a.call(onReadyRef);
        }
    }, [measuredWidth, originalSize, ready]);
    const resolvedBackgroundColor = wrapperBackgroundColor === null
        ? 'transparent'
        : (wrapperBackgroundColor !== null && wrapperBackgroundColor !== void 0 ? wrapperBackgroundColor : color.gray10);
    const fastImageSource = {
        uri: imageUrl,
        priority: priority === 'high'
            ? FastImage.priority.high
            : priority === 'low'
                ? FastImage.priority.low
                : FastImage.priority.normal,
    };
    const aspectRatio = originalSize
        ? originalSize.width / originalSize.height
        : undefined;
    if (fixedHeight != null) {
        return (_jsx(FastImage, { source: fastImageSource, resizeMode: RESIZE_MODE_MAP[resizeMode], onLoad: handleImageLoad, onError: () => setReady(true), style: [
                { height: fixedHeight, backgroundColor: resolvedBackgroundColor },
                aspectRatio != null ? { aspectRatio } : undefined,
                style,
            ] }));
    }
    const imageHeight = measuredWidth !== undefined && originalSize !== undefined
        ? (measuredWidth / originalSize.width) * originalSize.height
        : undefined;
    return (_jsx(ImageWrapper, { style: [
            imageHeight !== undefined
                ? { height: imageHeight }
                : aspectRatio != null
                    ? { aspectRatio }
                    : undefined,
            { backgroundColor: resolvedBackgroundColor },
            style,
        ], onLayout: handleLayout, children: _jsx(FastImage, { source: fastImageSource, resizeMode: RESIZE_MODE_MAP[resizeMode], onLoad: handleImageLoad, onError: () => setReady(true), style: { width: '100%', height: '100%' } }) }));
}
/**
 * 이미지를 미리 fetch하여 캐시에 저장.
 */
export function prefetchRemoteImage(url) {
    if (imageSizeCache.has(url))
        return;
    FastImage.preload([{ uri: url }]);
}
const ImageWrapper = styled.View({
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
});
